<script setup lang="ts">
const route = useRoute()
const app = useNuxtApp()
const storeIndex = useStoreIndex()
const storeArticleDetail = useStoreArticleDetail()

definePageMeta({
  layout: 'layout-side-default',
  validate: (route) => {
    return route.params.article === 'event' ? true : false
  },
})

const pageData = {
  eventItem: null as Article | null,
  eventObjects: null as ArticleObject[] | null,
  eventEyeCatch: null as ImageObject | null,

  memoItems: null as Memo[] | null,
  meta: {} as ApiMeta,

  eventNowCount: 2,
  eventFutureCount: 1,
  rating: 3,

  sPlace: '', // 場所
  sIrregularHoliday: null, // 本日開館中情報
  aSlide: null, // スライド情報

  pageSize: 6,
  pageCount: 0,
}

const refData = reactive({ sSlideCaption: null })

const setHead = () => {
  const headTitle = `${pageData.eventItem?.title}`
  const headDescription = `${pageData.eventItem?.title}の内容、会期、料金、休館日など、展覧会やイベントの詳細を紹介。チケットプレゼントや取材レポートも発信します。ミュージアム情報サイト、アイエム［インターネットミュージアム］による情報ページです。`
  // ぱんくず作成
  storeIndex.setBreadCrumbs([
    {
      text: '展覧会',
      disabled: false,
      to: `/event`,
    },
    {
      text: pageData.eventItem?.title,
      disabled: false,
      to: `/event/${pageData.eventItem?.article_slug}`,
    },
    {
      text: 'みんなの感想',
      disabled: true,
    },
  ])

  const aMeta = [{
    name: 'description',
    content: headDescription,
  }]
  //  og:imageはeye_catch_image_objectがあるときだけ
  if (pageData.eventEyeCatch?.url) {
    aMeta.push(
      {
        name: 'og:image',
        content: pageData.eventEyeCatch.url,
      },
    )
  }
  //  previewは noindex をつける
  if ((route.name as string).includes('preview')) {
    aMeta.push(
      {
        name: 'robots',
        content: 'noindex',
      },
    )
  }

  useHead({
    title: `${headTitle} | みんなの感想 | 展覧会`,
    meta: aMeta,
  })
}

// スライド変更イベント
const onChangeSlide = (slideNO) => {
  refData.sSlideCaption = pageData.aSlide[slideNO].sCaption
}

const initPage = async () => {
  const eventSlug = route.params.slug as string
  const fetchDetailOptions: KjdsFetchDetailOptions = {
    fetchParams: {
      articleTypeSlug: 'event',
      articleSlugOrId: eventSlug,
    },
  }

  const [_resSite, _resMaster, resEvent] = await Promise.all([
    storeIndex.fetchSite(),
    storeIndex.fetchMaster(),
    storeArticleDetail.fetchDetail(fetchDetailOptions),
  ])
  pageData.eventItem = resEvent.data.article
  pageData.eventObjects = resEvent.data.article_objects
  pageData.eventEyeCatch = storeIndex.getImgInfoByEyeCatch(pageData.eventItem.eye_catch_image_object)

  if (!pageData.sPlace) {
    pageData.sPlace = app.$getEventPlace(pageData.eventItem)
  }

  //  メモの読み込み
  const pageSize = 6
  const page = app.$anyToInteger(route.query.page, 1)

  const memoRes = await app.$MymuseumMemoApi.mymuseumMemoAllList(
    eventSlug,
    page,
    pageSize,
  )
  if ((memoRes.data as any).code !== 200000) {
    throw createError({ statusCode: 940 })
    return
  }
  pageData.memoItems = (memoRes.data as any).data.mymuseum_memos
  pageData.meta = (memoRes.data as any).meta

  // イベントからスライド作成
  if (import.meta.client === true) {
    const custom = pageData.eventItem.custom as any
    if (pageData.eventItem && !pageData.aSlide) {
      if (custom && pageData.eventObjects) { // 追加
        pageData.aSlide = []
        app.$addSlides(
          pageData.eventItem,
          'event_slide',
          10,
          pageData.eventObjects,
          pageData.aSlide,
          (pageData.eventItem.is_old === 1) ? null : 'l',
        )
      }
      if (pageData.eventItem.eye_catch_image_object) {
        app.$initSlide(pageData.eventItem.eye_catch_image_object, pageData.aSlide)
      }
    }
  }
}
await initPage()
setHead()

onMounted(() => {
  if (pageData.aSlide?.length === 1) {
    onChangeSlide(0)
  }
})
</script>

<template>
  <div
    v-if="pageData.eventItem"
    class="p-articleDetailPage"
  >
    <div class="l-mainOverview">
      <div class="c-header">
        <div>
          <p
            v-if="pageData.eventItem.custom"
            class="c-header_subtitle"
          >
            {{ pageData.eventItem.custom.event_subtitle }}
          </p>
          <h1
            v-if="pageData.eventItem.title"
            class="c-header_title"
          >
            {{ pageData.eventItem.title }}
            <span
              v-if="pageData.eventItem.custom.is_event === '1'"
              class="c-header_title_label"
            >
              イベント
            </span>
          </h1>
          <p
            v-if="pageData.eventItem.custom"
            class="c-header_subtitle2"
          >
            {{ pageData.eventItem.custom.event_subtitle2 }}
          </p>
          <p
            v-if="pageData.sPlace"
            class="c-header_location"
          >
            {{ pageData.sPlace }}
          </p>
        </div>
      </div>

      <div
        v-if="pageData.sIrregularHoliday"
        class="c-irregularHoliday"
      >
        <div
          class="c-irregularHoliday_text"
          v-html="pageData.sIrregularHoliday"
        />
      </div>

      <client-only>
        <div
          v-if="pageData.aSlide?.length > 0"
          class="c-slide"
        >
          <v-carousel
            v-if="pageData.aSlide.length > 1"
            class="IMCustomCarousel"
            hide-delimiter-background
            cycle
            height="auto"
            v-on:update:model-value="onChangeSlide"
          >
            <v-carousel-item
              v-for="(record, index) in pageData.aSlide"
              v-bind:key="index"
            >
              <CommonImage
                class="c-slide_item_img"
                v-bind:src="record.oImg"
                v-bind:height="app.$device.isMobile ? '280' : '347'"
                aspect-ratio="3/2"
                object-fit="contain"
              />
            </v-carousel-item>
          </v-carousel>
          <CommonImage
            v-else
            class="c-slide_item_img"
            v-bind:src="pageData.aSlide[0].oImg"
            v-bind:height="app.$device.isMobile ? '280' : '347'"
            aspect-ratio="3/2"
            object-fit="contain"
          />
        </div>

        <div
          class="c-slide_imgLocation"
        >
          {{ refData.sSlideCaption }}
        </div>
      </client-only>
    </div>

    <div
      class="c-memolist"
    >
      <h2 class="c-memolist_title">
        <img
          class="c-memolist_title_img"
          src="@/assets/images/icon-edit-msg.svg"
        >
        みんなの感想
      </h2>
      <div
        class="c-memolist_body"
      >
        <template
          v-if="pageData.memoItems"
        >
          <div
            class="c-memolist_list"
          >
            <div
              v-for="memo in pageData.memoItems"
              v-bind:key="memo.id"
              class="c-memolist_item"
            >
              <div class="c-memolist_item_head">
                <div class="c-memolist_item_title c-clampLines is-clampline1">
                  {{ memo.title }}
                </div>
                <div
                  v-if="memo.score > 0"
                  class="c-memolist_item_star"
                >
                  <span>評価</span>
                  <v-rating
                    v-model="memo.score"
                    class="c-rating"
                    empty-icon="mdi-star-outline"
                    full-icon="mdi-star"
                    color="primary"
                    length="5"
                    size="16"
                    readonly
                  />
                </div>
              </div>
              <div class="c-memolist_item_info">
                <span class="c-memolist_item_name">{{ memo.nickname }}</span>
                <span class="c-memolist_item_date">{{ memo.arrival_date }}</span>
              </div>
              <div class="c-memolist_item_body">
                {{ memo.body }}
              </div>
            </div>
          </div>
          <Pagination
            v-bind:page="pageData.meta.page_no"
            v-bind:length="pageData.meta.total_page"
          />
        </template>
        <template
          v-else
        >
          <div
            class="c-memolist_empty"
          >
            感想はまだありません
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive" as responsive;

.l-mainOverview {
  min-height: 484px;

  @include responsive.mq_down(sp) {
    min-height: auto;
  }
}

.c-header {
  display: flex;
  font-family: var(--g_font_family_NotoSansCJKjp);

  &_subtitle {
    margin-top: 2px;
    margin-bottom: -3px;
    font-size: 14px;
  }

  &_title {
    margin-top: 2px;
    color: var(--color_text_base);
    font-size: 25px;
    font-weight: 400;

    @include responsive.mq_down(sp) {
      margin: 0 0 16px;
      font-size: 20px;
    }

    &_label {
      display: inline-block;
      height: 27px;
      margin-left: 15px;
      padding: 0 8px;
      border: solid 1px var(--color_primary);
      color: var(--color_primary);
      font-size: 14px;
      line-height: 25px;
      vertical-align: middle;
    }
  }

  &_subtitle2 {
    margin-top: 2px;
    font-size: 14px;

    @include responsive.mq_down(sp) {
      margin-top: -16px;
    }
  }

  &_location {
    margin-top: -6px;
    color: var(--color_text_base);
    font-size: 14px;
    font-weight: bold;
  }
}

.c-irregularHoliday {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 45px;
  padding: 1px 17px;
  border: 3px solid var(--color_primary);

  @include responsive.mq_down(sp) {
    padding: 1px 5px;
  }

  &_text {
    color: var(--color_primary !important);
    font-family: var(--g_font_family_NotoSansCJKjp);
    font-size: 14px;
    line-height: 1.4;
    text-align: center;
  }
}

.c-slide {
  margin-top: 30px;

  &_item {
    &_img {
      position: relative;
      width: 100%;
      background-color: #ebebeb;

      @include responsive.mq_down(sp) {
        height: 48vw;
      }
    }
  }

  &_imgLocation {
    display: flex;
    justify-content: flex-end;
    min-height: 58px;
    margin-top: 5px;
    color: var(--color_text_base);
    font-family: var(--g_font_family_NotoSansCJKjp);
    font-size: 14px;
    line-height: 2;
  }
}

.c-memolist {
  margin-top: 40px;

  &_empty {
    margin-bottom: 50px;
    padding: 30px 0;
    text-align: center;
  }

  &_list {
    margin-bottom: 60px;
  }

  &_title {
    padding: 12px 19px;
    background-color: var(--color_primary);
    color: #fff;
    font-size: 20px;
    line-height: 24px;

    &_img {
      width: auto;
      height: 24px;
      margin-right: 8px;
    }
  }

  &_item {
    position: relative;
    width: 100%;
    margin-top: 30px;
    padding: 20px;
    border: solid 1px var(--color_border_control);
    border-radius: 6px;
    font-family: var(--g_font_family_NotoSansCJKjp);

    &_head {
      display: flex;
      padding-bottom: 15px;
      border-bottom: solid 1px var(--color_border_control);
    }

    &_title {
      flex-grow: 1;
      padding-right: 10px;
      font-size: 18px;
      font-weight: bold;
      line-height: 1.56;
    }

    &_star {
      display: flex;
      flex-shrink: 0;

      span {
        margin-right: 30px;
      }
    }

    &_info {
      display: flex;
      position: relative;
      padding: 15px 0 12px;
      font-size: 14px;
      line-height: 1.5;
    }

    &_name {
      font-weight: bold;
    }

    &_body {
      font-size: 14px;
      line-height: 1.5;
    }

    &_id {
      position: absolute;
      top: 15px;
      right: 10px;
      color: #ccc;
      font-size: 14px;
    }
  }
}

.c-rating {
  &:deep(.v-rating) {
    padding: 2px 6px;
    background-color: var(--color_background_whiteblue);
  }

  &:deep(.v-icon) {
    padding: 2px !important;
  }
}
</style>
